<template>
  <div class="hosted-account-receive-component">
    <div
      class="alert alert-subtle-secondary d-flex align-items-start gap-3 mb-4"
      role="alert"
    >
      <FontAwesomeIcon
        icon="fas fa-share-from-square"
        class="mt-1 flex-shrink-0"
      />
      <div class="flex-grow-1">
        <p class="fw-semibold mb-1">
          {{ $t("hosted_accounts.account_details_heading") }}
        </p>
        <p class="small mb-2 opacity-75">
          {{ $t("hosted_accounts.description") }}
        </p>
        <div class="d-flex flex-wrap gap-3 align-items-center">
          <div class="d-flex align-items-center gap-2">
            <span class="text-muted small"
              >{{ $t("hosted_accounts.company_name") }}:</span
            >
            <button
              type="button"
              class="copy-value-btn"
              :aria-label="$t('hosted_accounts.company_name')"
              @click="copyHostedCompanyName"
            >
              <span class="copy-value-text">{{ SOLDERA_COMPANY_NAME }}</span>
              <FontAwesomeIcon icon="fas fa-copy" class="copy-value-icon" />
            </button>
          </div>
          <div class="d-flex align-items-center gap-2">
            <span class="text-muted small"
              >{{ $t("hosted_accounts.eecs_account") }}:</span
            >
            <button
              type="button"
              class="copy-value-btn"
              :aria-label="$t('hosted_accounts.eecs_account')"
              @click="copyHostedEecsAccount"
            >
              <span class="copy-value-text">{{ hostedAccountEecs }}</span>
              <FontAwesomeIcon icon="fas fa-copy" class="copy-value-icon" />
            </button>
          </div>
        </div>
      </div>
    </div>

    <form @submit.prevent="handleSubmit">
      <div class="card mb-4">
        <div class="card-header bg-light">
          <span class="fw-semibold">
            {{ $t("hosted_accounts.transfer_identification_heading") }}
          </span>
        </div>
        <div class="card-body">
          <fieldset class="identifier-fieldset">
            <div class="row g-3">
              <div class="col-12 col-md-4">
                <label for="amount" class="form-label small">
                  {{ $t("hosted_accounts.amount_mwh") }}
                </label>
                <input
                  id="amount"
                  v-model.number="formAmount"
                  type="number"
                  class="form-control"
                  :class="{ 'is-invalid': v$.formAmount.$error }"
                  min="1"
                  aria-describedby="identifierHint"
                  @blur="v$.formAmount.$touch"
                />
                <div
                  v-for="error of v$.formAmount.$errors"
                  :key="error.$uid"
                  class="invalid-feedback"
                >
                  {{ error.$message }}
                </div>
              </div>

              <div class="col-12 col-md-4">
                <label for="senderCompanyName" class="form-label small">
                  {{ $t("hosted_accounts.sender_company_name") }}
                </label>
                <GlobalCompanySearchDropdown
                  v-model="formSenderCompanyName"
                  :withLabel="false"
                  :withDescription="false"
                  :class="{ 'is-invalid': v$.formSenderCompanyName.$error }"
                  aria-describedby="identifierHint"
                  @select="handleCompanySelected"
                  @blur="v$.formSenderCompanyName.$touch"
                />
                <div
                  v-for="error of v$.formSenderCompanyName.$errors"
                  :key="error.$uid"
                  class="invalid-feedback"
                >
                  {{ error.$message }}
                </div>
              </div>

              <div class="col-12 col-md-4">
                <label for="senderEecsAccount" class="form-label small">
                  {{ $t("hosted_accounts.sender_eecs_account") }}
                </label>
                <input
                  id="senderEecsAccount"
                  v-model="formSenderEecsAccount"
                  type="text"
                  class="form-control"
                  :class="{ 'is-invalid': v$.formSenderEecsAccount.$error }"
                  maxlength="50"
                  aria-describedby="identifierHint"
                  @blur="v$.formSenderEecsAccount.$touch"
                />
                <div
                  v-for="error of v$.formSenderEecsAccount.$errors"
                  :key="error.$uid"
                  class="invalid-feedback"
                >
                  {{ error.$message }}
                </div>
              </div>
            </div>
            <p
              id="identifierHint"
              class="small mt-3 mb-0"
              :class="
                showIdentifierWarning ? 'text-danger fw-semibold' : 'text-muted'
              "
            >
              <FontAwesomeIcon
                :icon="
                  hasAtLeastOneIdentifier
                    ? 'fas fa-circle-check'
                    : 'fas fa-circle-info'
                "
                class="me-1"
                :class="hasAtLeastOneIdentifier ? 'text-success' : ''"
              />
              {{ $t("receive_order.at_least_one_required") }}
            </p>
          </fieldset>
        </div>
      </div>

      <div class="mb-3" style="max-width: 400px">
        <label for="counterpartyEmail" class="form-label">
          {{ $t("hosted_accounts.counterparty_email") }}
          <span class="text-danger" aria-hidden="true">*</span>
        </label>
        <input
          id="counterpartyEmail"
          v-model="formCounterpartyEmail"
          type="email"
          class="form-control"
          :class="{ 'is-invalid': v$.formCounterpartyEmail.$error }"
          placeholder="sender@company.com"
          required
          aria-required="true"
          @blur="v$.formCounterpartyEmail.$touch"
        />
        <div
          v-for="error of v$.formCounterpartyEmail.$errors"
          :key="error.$uid"
          class="invalid-feedback"
        >
          {{ error.$message }}
        </div>
      </div>

      <div class="mb-4">
        <button
          type="button"
          class="btn btn-sm btn-link text-muted px-0"
          @click="showAdvanced = !showAdvanced"
        >
          <FontAwesomeIcon
            :icon="
              showAdvanced ? 'fas fa-chevron-down' : 'fas fa-chevron-right'
            "
            class="me-1"
            size="xs"
          />
          {{ $t("hosted_accounts.advanced_options") }}
        </button>

        <div v-show="showAdvanced" class="row g-3 mt-1">
          <div class="col-12 col-md-6">
            <label for="repeatingInterval" class="form-label small">
              {{ $t("receive_order.repeating_label") }}
            </label>
            <select
              id="repeatingInterval"
              v-model="formRepeatingInterval"
              class="form-select form-select-sm"
            >
              <option value="NONE">
                {{ $t("receive_order.repeating_options.none") }}
              </option>
              <option value="MONTHLY">
                {{ $t("receive_order.repeating_options.monthly") }}
              </option>
              <option value="QUARTERLY">
                {{ $t("receive_order.repeating_options.quarterly") }}
              </option>
              <option value="HALF_YEAR">
                {{ $t("receive_order.repeating_options.half_yearly") }}
              </option>
              <option value="YEARLY">
                {{ $t("receive_order.repeating_options.yearly") }}
              </option>
            </select>
          </div>

          <div class="col-12 col-md-6">
            <label for="operationNotes" class="form-label small">
              {{ $t("hosted_accounts.optional_notes_on_email") }}
            </label>
            <textarea
              id="operationNotes"
              v-model="formOperationNotes"
              class="form-control form-control-sm"
              rows="1"
              maxlength="500"
              :placeholder="$t('hosted_accounts.operation_notes_placeholder')"
            ></textarea>
          </div>
        </div>
      </div>

      <div class="pt-3 border-top">
        <button
          type="submit"
          class="btn btn-primary"
          :disabled="isLoading || !isFormValid"
        >
          <span
            v-if="isLoading"
            class="spinner-border spinner-border-sm me-2"
            role="status"
            aria-hidden="true"
          ></span>
          <FontAwesomeIcon v-else icon="fas fa-paper-plane" class="me-2" />
          {{ $t("hosted_accounts.create_order") }}
        </button>
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import GlobalCompanySearchDropdown from "@/components/common/GlobalCompanySearchDropdown.vue";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { useValidators } from "@/composables/useValidators";
import type {
  GoReceiveOrderRequest,
  SolderaRegistryAccount,
} from "@/dashboard/inventory_management/company_orders/types/virtualAccountTypes";
import type { Company, PickedCompany } from "@/types";
import { SOLDERA_COMPANY_NAME } from "@/money/money_constants";
import { copyToClipboard } from "@/utils/copyUtils";
import useVuelidate from "@vuelidate/core";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { createReceiveOrder } from "../common/registryApi";

const props = defineProps<{
  company: Company;
  selectedCountry: string;
  registryAccounts: SolderaRegistryAccount[];
}>();

const emit = defineEmits<{
  loading: [isLoading: boolean];
}>();

const { t } = useI18n();
const { globalErrorHandler, globalSuccessHandler } = useGlobalHandlers();
const { required, email, integer, min } = useValidators(t);

const isLoading = ref(false);
const showAdvanced = ref(false);
const hasAttemptedSubmit = ref(false);

const formAmount = ref<number | null>(null);
const formSenderCompanyName = ref("");
const formSenderEecsAccount = ref("");
const formCounterpartyEmail = ref("");
const formOperationNotes = ref("");
const formRepeatingInterval = ref<string>("NONE");

const hostedAccountEecs = computed(() => {
  const account = props.registryAccounts.find(
    (acc) => acc.country_code === props.selectedCountry
  );
  return account?.eecs ?? "";
});

const hasAtLeastOneIdentifier = computed(
  () =>
    (formAmount.value !== null && formAmount.value > 0) ||
    formSenderCompanyName.value.trim() !== "" ||
    formSenderEecsAccount.value.trim() !== ""
);

const showIdentifierWarning = computed(
  () => hasAttemptedSubmit.value && !hasAtLeastOneIdentifier.value
);

const isFormValid = computed(
  () =>
    hasAtLeastOneIdentifier.value &&
    formCounterpartyEmail.value.trim() !== "" &&
    !v$.value.formCounterpartyEmail.$error &&
    (formAmount.value === null || !v$.value.formAmount.$error)
);

const validationRules = {
  formAmount: {
    integer,
    min: min(1),
  },
  formSenderCompanyName: {},
  formSenderEecsAccount: {},
  formCounterpartyEmail: { required, email },
};

const v$ = useVuelidate(validationRules, {
  formAmount,
  formSenderCompanyName,
  formSenderEecsAccount,
  formCounterpartyEmail,
});

const handleCompanySelected = (company: PickedCompany) => {
  formSenderCompanyName.value = company.name;
  formSenderEecsAccount.value = company.account_eecs ?? "";
};

const copyHostedCompanyName = () => {
  copyToClipboard(
    SOLDERA_COMPANY_NAME,
    t("hosted_accounts.company_name_copied"),
    globalSuccessHandler,
    globalErrorHandler,
    t("hosted_accounts.copy_fallback"),
    t("hosted_accounts.copy_failed")
  );
};

const copyHostedEecsAccount = () => {
  copyToClipboard(
    hostedAccountEecs.value,
    t("hosted_accounts.eecs_account_copied"),
    globalSuccessHandler,
    globalErrorHandler,
    t("hosted_accounts.copy_fallback"),
    t("hosted_accounts.copy_failed")
  );
};

const handleSubmit = async () => {
  hasAttemptedSubmit.value = true;
  v$.value.$touch();

  if (!isFormValid.value) return;

  isLoading.value = true;
  emit("loading", true);

  try {
    const orderRequest: GoReceiveOrderRequest = {
      amount: formAmount.value || undefined,
      sender_company_name: formSenderCompanyName.value || undefined,
      sender_eecs_account: formSenderEecsAccount.value || undefined,
      counterparty_email: formCounterpartyEmail.value,
      recipient_country: props.selectedCountry,
      is_virtual_account: true,
      operation_notes: formOperationNotes.value || undefined,
      repeating_interval:
        formRepeatingInterval.value === "NONE"
          ? undefined
          : formRepeatingInterval.value,
    };

    if (!props.company.id) {
      globalErrorHandler(new Error(t("hosted_accounts.company_id_missing")));
      return;
    }

    const response = await createReceiveOrder(props.company.id, orderRequest);

    globalSuccessHandler(t("common.success"), response.message);
    resetForm();
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    isLoading.value = false;
    emit("loading", false);
  }
};

const resetForm = () => {
  formAmount.value = null;
  formSenderCompanyName.value = "";
  formSenderEecsAccount.value = "";
  formCounterpartyEmail.value = "";
  formOperationNotes.value = "";
  formRepeatingInterval.value = "NONE";
  hasAttemptedSubmit.value = false;
  showAdvanced.value = false;
  v$.value.$reset();
};
</script>

<style scoped lang="scss">
.hosted-account-receive-component {
  .copy-value-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.375rem 0.75rem;
    background: var(--bs-white);
    border: 1px solid var(--bs-border-color);
    border-radius: var(--bs-border-radius);
    cursor: pointer;
    transition:
      border-color 0.15s ease,
      box-shadow 0.15s ease;

    &:hover {
      border-color: var(--phoenix-primary);
      box-shadow: 0 0 0 1px var(--phoenix-primary);
    }

    &:focus-visible {
      outline: 2px solid var(--phoenix-primary);
      outline-offset: 2px;
    }
  }

  .copy-value-text {
    font-family: var(--bs-font-monospace);
    font-size: 0.9375rem;
    font-weight: 600;
    letter-spacing: 0.01em;
    color: var(--bs-body-color);
  }

  .copy-value-icon {
    font-size: 0.75rem;
    color: var(--phoenix-primary);
    opacity: 0.7;
    transition: opacity 0.15s ease;

    .copy-value-btn:hover & {
      opacity: 1;
    }
  }

  .identifier-fieldset {
    border: none;
    padding: 0;
    margin: 0;

    legend {
      float: none;
      width: auto;
    }
  }
}
</style>
